import { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Button } from "../Items/Button";
import { Input } from "../Items/Input";
import { Select } from "../Items/Select";
import Thumbnail from "../Items/Thumbnail";
import { getPictureList, getSignedUrl } from "../../server";
import { useMQTTPublish, useMQTTSubscribe } from "../../hooks/mqtt";

// One column that every row aligns to, so edges line up down the page.
const Page = styled.div`
  width: min(1100px, calc(100% - 32px));
  margin: 0 auto 3rem;
  text-align: left;
`;

const Section = styled.section`
  padding: 1.5rem 0;

  & + & {
    border-top: 2px solid rgb(13 25 65 / 10%);
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 0.85em;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: gray;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75em;
`;

const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75em;
`;

// Input is content-box (Items/Input.js); border-box makes it the same 3em as
// the buttons and selects beside it.
const BoxInput = styled(Input)`
  box-sizing: border-box;
  width: ${(props) => props.$width || "auto"};
`;

const Unit = styled.span`
  color: gray;
`;

const ToolButton = styled(Button)`
  flex-shrink: 0;
  ${(props) => props.disabled && `background-color: rgb(169 169 169 / 50%);`}
  &:active {
    background-color: rgb(0 79 255 / 50%);
  }
`;

const IconButton = styled(ToolButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3em;
  padding: 0;
`;

// Drawn rather than typed: arrow glyphs sit off-centre by a font-dependent amount.
function Chevron({ left }) {
  return (
    <svg width="1.1em" height="1.1em" viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d={left ? "M8 2 4 6l4 4" : "M4 2l4 4-4 4"} />
    </svg>
  );
}

const StepButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  ${(props) => props.$side}: 0.75em;
  transform: translateY(-50%);
  border-radius: 50%;
  box-shadow: 0 2px 6px rgb(0 0 0 / 25%);

  &:disabled {
    visibility: hidden;
  }
`;

const HourGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 2px 3px;
  margin-top: 1.25rem;
`;

const HourBar = styled.button`
  position: relative;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: linear-gradient(
    to top,
    ${(props) => (props.$selected ? "rgb(0 79 255)" : "#529fce")} ${(props) => props.$fill}%,
    rgb(13 25 65 / 5%) ${(props) => props.$fill}%
  );
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const VideoDot = styled.span`
  position: absolute;
  top: 4px;
  left: 50%;
  width: 6px;
  height: 6px;
  margin-left: -3px;
  border-radius: 50%;
  background-color: rgb(255 140 0);
`;

const HourLabel = styled.span`
  font-size: 0.75em;
  color: gray;
  white-space: nowrap;
`;

// Fixed size whatever the picture, so nothing below it moves.
const Stage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 65vh;
  border-radius: 10px;
  background-color: rgb(13 25 65 / 6%);
  overflow: hidden;
  touch-action: pan-y pinch-zoom;

  /* On an upright phone a landscape picture is limited by the width anyway,
     so the extra height only goes to portrait pictures. */
  @media (max-aspect-ratio: 1 / 1) {
    aspect-ratio: 4 / 5;
  }

  /* Touch screens swipe instead. */
  @media (hover: none) {
    ${StepButton} {
      display: none;
    }
  }

  @media (hover: hover) {
    ${StepButton} {
      opacity: 0;
      transition: opacity 0.2s;
    }

    &:hover ${StepButton}, ${StepButton}:focus-visible {
      opacity: 1;
    }
  }
`;

// Holds the current picture, with its neighbours just out of view either side.
const Track = styled.div`
  position: absolute;
  inset: 0;
  will-change: transform;
`;

const Pane = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.$offset}%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoPlaceholder = styled.span`
  color: gray;
  font-weight: bold;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 3em;
  height: 3em;
  margin: -1.5em;
  border: 4px solid rgb(255 255 255 / 40%);
  border-top-color: rgb(0 79 255);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  pointer-events: none;
`;

const VideoPlayer = styled.video`
  display: block;
  max-width: 100%;
  max-height: 100%;
`;

const ImagePlayer = styled.img`
  display: block;
  max-width: 100%;
  max-height: 100%;
`;

const Scrubber = styled.input`
  display: block;
  width: 100%;
  margin: 1rem 0 0;
  accent-color: rgb(0 79 255);
`;

const Caption = styled.p`
  margin: 0.75rem 0 0;
  text-align: center;
`;

const HourHeader = styled.h3`
  margin: 1.25rem 0 0.75rem;
  font-size: 1em;
`;

const ThumbnailGrid = styled.div`
  margin-top: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
`;

const StatusDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.status === true ? "green" : "gray"};
`;

const COOLDOWN_MS = 3000;
const SWIPE_PX = 50;
const VIDEO_CONTROLS_PX = 60;
const SLIDE_MS = 200;
// Signed URLs are issued for 15 minutes; reuse them well inside that.
const URL_TTL_MS = 10 * 60 * 1000;

function moveTrack(track, percent, px, animate) {
  if (!track) return;
  track.style.transition = animate ? `transform ${SLIDE_MS}ms ease-out` : "none";
  track.style.transform = `translateX(calc(${percent}% + ${px}px))`;
}

// Filenames carry the camera and a UTC timestamp, e.g. "camera_0-2023_06_19-14_35_10.jpg".
const PICTURE_NAME = /^(.*?)-?(\d{4})_(\d{2})_(\d{2})-(\d{2})_(\d{2})_(\d{2})/;

function parsePicture(filename) {
  const match = PICTURE_NAME.exec(filename);
  if (!match) return null;
  const [, camera, year, month, day, hour, minute, second] = match;
  const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
  if (isNaN(date.getTime())) return null;
  return { filename, camera, date, isVideo: filename.includes(".mp4") };
}

const pad = (n) => String(n).padStart(2, "0");

// Local day, in the "YYYY-MM-DD" form <input type="date"> uses.
const dayKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

function atTimeOfDay(key, date) {
  const time = new Date(`${key}T00:00`);
  time.setHours(date.getHours(), date.getMinutes(), date.getSeconds());
  return time;
}

function nearest(pictures, time) {
  return pictures.reduce((best, picture) =>
    Math.abs(picture.date - time) < Math.abs(best.date - time) ? picture : best
  );
}

function groupByDay(pictures) {
  const days = new Map();
  for (const picture of pictures) {
    const key = dayKey(picture.date);
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(picture);
  }
  return days;
}

export default function Camera({ wecs, mqttClient, messages }) {
  const [pictureList, setPictureList] = useState(null);
  const [listError, setListError] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [camera, setCamera] = useState("");
  const [videoDuration, setVideoDuration] = useState(10);
  const [takingPicture, setTakingPicture] = useState(false);
  const [takingVideo, setTakingVideo] = useState(false);
  const [error, setError] = useState("");
  const [urls, setUrls] = useState({});
  const [loaded, setLoaded] = useState({});
  const [selectedHostname, setSelectedHostname] = useState(wecs?.[0]?.hostname);
  const wec = wecs?.find((wec) => wec.hostname === selectedHostname);
  const publish = useMQTTPublish(mqttClient);

  const onCameraMessage = useCallback((data) => {
    try {
      const payload = JSON.parse(data);
      const items = Array.isArray(payload) ? payload : [payload];
      const newFiles = items.map((i) => i.filename).filter((f) => f);

      if (newFiles.length > 0) {
        console.log("onNewPicture (MQTT):", newFiles);
        setPictureList(
          (oldList) =>
            oldList && [...newFiles.filter((f) => !oldList.includes(f)), ...oldList]
        );
      }
    } catch (e) {
      console.error("Failed to parse camera message", e);
    }
  }, []);

  useMQTTSubscribe(
    mqttClient,
    wec ? `weco/${wec.hostname}/camera` : null,
    onCameraMessage
  );

  useEffect(() => {
    (async function () {
      try {
        const pictureList = await getPictureList();
        console.log("Picture list:", pictureList);
        setPictureList(pictureList);
      } catch (e) {
        console.error("Getting picture list error", e);
        setListError(true);
      }
    })();
  }, []);

  // The server orders by upload time, which a backlog can scramble, so sort
  // on the timestamp in the name.
  const allPictures = useMemo(
    () => (pictureList ?? []).map(parsePicture).filter(Boolean).sort((a, b) => a.date - b.date),
    [pictureList]
  );
  const cameras = useMemo(
    () => [...new Set(allPictures.map((p) => p.camera))].sort(),
    [allPictures]
  );
  const pictures = useMemo(
    () => (camera ? allPictures.filter((p) => p.camera === camera) : allPictures),
    [allPictures, camera]
  );
  const days = useMemo(() => groupByDay(pictures), [pictures]);

  const index = pictures.findIndex((p) => p.filename === currentFile);
  const current = pictures[index];
  const latest = pictures[pictures.length - 1];

  // Tracked by filename so MQTT arrivals never move the view.
  useEffect(() => {
    if (!current && latest) setCurrentFile(latest.filename);
  }, [current, latest]);

  const gesture = useRef(null);
  const sliding = useRef(false);
  const trackRef = useRef(null);
  const pending = useRef(new Set());

  // A failed fetch is kept as { url: null } so it isn't retried on every render.
  const urlEntry = useCallback((filename) => {
    const entry = urls[filename];
    return entry && Date.now() - entry.at < URL_TTL_MS ? entry : null;
  }, [urls]);

  const fetchUrl = useCallback((filename) => {
    if (!filename || pending.current.has(filename)) return;
    pending.current.add(filename);
    const store = (url) =>
      setUrls((urls) => ({ ...urls, [filename]: { url, at: Date.now() } }));
    getSignedUrl(filename)
      .then(store)
      .catch((e) => {
        console.error("Getting signed URL error", e);
        store(null);
      })
      .finally(() => pending.current.delete(filename));
  }, []);

  const step = useCallback(
    (delta) =>
      setCurrentFile((file) => {
        const i = pictures.findIndex((p) => p.filename === file);
        return pictures[i + delta]?.filename ?? file;
      }),
    [pictures]
  );

  useEffect(() => {
    function onKeyDown(event) {
      if (event.target.closest?.("input, select, textarea, video")) return;
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "ArrowRight") step(1);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step]);

  const viewedFile = current?.filename;
  const prev = pictures[index - 1];
  const next = pictures[index + 1];

  useEffect(() => {
    if (!urlEntry(viewedFile)) fetchUrl(viewedFile);
  }, [viewedFile, urlEntry, fetchUrl]);

  // The neighbours load into their panes so a swipe slides onto a picture
  // that's already there. Delayed so scrubbing doesn't download each one.
  useEffect(() => {
    const timer = setTimeout(() => {
      for (const picture of [prev, next]) {
        if (picture && !picture.isVideo && !urlEntry(picture.filename)) fetchUrl(picture.filename);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [prev, next, urlEntry, fetchUrl]);

  // Before paint, so the pane slid to becomes the centred one without a jump.
  useLayoutEffect(() => {
    moveTrack(trackRef.current, 0, 0, false);
  }, [viewedFile]);

  // The broker acks within milliseconds, so the button is held disabled for a
  // fixed cooldown instead to give visible feedback and stop rapid re-sends.
  function sendEvent(event, setBusy) {
    setBusy(true);
    setTimeout(() => setBusy(false), COOLDOWN_MS);
    publish(`weco/${wec.hostname}/event`, JSON.stringify(event), {}, (err) => {
      if (err) {
        console.error(`${event.type} publish error:`, err);
        setError(`Failed to send ${event.type} command`);
      } else {
        setError("");
      }
    });
  }

  const cameraSection = (
    <Section>
      <SectionTitle>Camera</SectionTitle>
      <Toolbar>
        <Group>
          <StatusDot status={messages?.[wec?.hostname]?.status?.status?.connected} />
          <Select
            value={selectedHostname || ""}
            onChange={(event) => {
              console.log("Setting selected wec to", event.target.value);
              setSelectedHostname(event.target.value);
              setTakingPicture(false);
              setTakingVideo(false);
              setError("");
            }}
          >
            {wecs?.map((wec) => (
              <option key={wec.id || wec.hostname} value={wec.hostname}>
                {wec.name || wec.hostname || wec.id}
              </option>
            ))}
          </Select>
        </Group>
        <Group>
          <Button
            onClick={() => sendEvent({ type: "take_picture" }, setTakingPicture)}
            disabled={takingPicture || !wec}
          >
            {takingPicture ? "Taking picture…" : "Take picture"}
          </Button>
          <Button
            onClick={() =>
              sendEvent({ type: "take_video", duration: videoDuration }, setTakingVideo)
            }
            disabled={takingVideo || !wec}
          >
            {takingVideo ? "Taking video…" : "Take video"}
          </Button>
          <BoxInput
            type="number"
            min={1}
            $width="5.5em"
            value={videoDuration}
            onChange={(e) => setVideoDuration(e.target.value)}
          />
          <Unit>seconds</Unit>
        </Group>
      </Toolbar>
      {error && <p style={{ color: "red" }}>{error}</p>}
    </Section>
  );

  if (!current) {
    const loading = !listError && (!pictureList || pictures.length > 0);
    return (
      <Page>
        {cameraSection}
        <Section>
          <SectionTitle>Gallery</SectionTitle>
          <Stage>{loading && <Spinner />}</Stage>
          <Caption>
            {listError ? "Couldn't load the pictures" : loading ? "Loading pictures…" : "No pictures yet"}
          </Caption>
        </Section>
      </Page>
    );
  }

  const select = (picture) => setCurrentFile(picture.filename);

  // A video's controls along its bottom edge take horizontal drags for
  // seeking, so swipes starting there are left to them.
  function onTouchStart(event) {
    const touch = event.touches[0];
    const video = event.target.closest("video");
    const onControls =
      video && touch.clientY > video.getBoundingClientRect().bottom - VIDEO_CONTROLS_PX;
    gesture.current =
      event.touches.length === 1 && !sliding.current && !onControls
        ? { x: touch.clientX, y: touch.clientY, dx: 0, axis: null }
        : null;
  }

  function onTouchMove(event) {
    const g = gesture.current;
    if (!g) return;
    const touch = event.touches[0];
    const dx = touch.clientX - g.x;
    const dy = touch.clientY - g.y;
    if (!g.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 8)
      g.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    if (g.axis !== "x") return;
    g.dx = dx;
    // Past either end the picture only gives a little, then springs back.
    const neighbour = dx < 0 ? next : prev;
    moveTrack(trackRef.current, 0, neighbour ? dx : dx / 3, false);
  }

  function onTouchEnd() {
    const g = gesture.current;
    gesture.current = null;
    if (g?.axis !== "x") return;
    const direction = g.dx < 0 ? 1 : -1;
    if (Math.abs(g.dx) < SWIPE_PX || !(direction > 0 ? next : prev)) {
      moveTrack(trackRef.current, 0, 0, true);
      return;
    }
    sliding.current = true;
    moveTrack(trackRef.current, -direction * 100, 0, true);
    setTimeout(() => {
      sliding.current = false;
      step(direction);
    }, SLIDE_MS);
  }

  const markLoaded = (url) => setLoaded((loaded) => ({ ...loaded, [url]: true }));

  function renderPane(picture) {
    const entry = urlEntry(picture.filename);
    const url = entry?.url;
    if (picture.isVideo && picture !== current)
      return <VideoPlaceholder>▶ Video</VideoPlaceholder>;
    return (
      <>
        {url &&
          (picture.isVideo ? (
            <VideoPlayer
              controls
              autoPlay
              src={url}
              onLoadedData={() => markLoaded(url)}
              onError={() => markLoaded(url)}
            />
          ) : (
            <ImagePlayer src={url} onLoad={() => markLoaded(url)} onError={() => markLoaded(url)} />
          ))}
        {(entry ? url && !loaded[url] : true) && <Spinner />}
      </>
    );
  }

  const day = dayKey(current.date);
  const dayKeys = [...days.keys()];
  const dayIndex = dayKeys.indexOf(day);
  const hours = Array.from({ length: 24 }, () => []);
  days.get(day).forEach((p) => hours[p.date.getHours()].push(p));
  const hour = current.date.getHours();
  const hourPictures = hours[hour];
  const busiestHour = Math.max(...hours.map((h) => h.length));

  // Keeps the time of day, so stepping days compares like with like. A day
  // with no pictures falls through to the nearest one that has some.
  function showDay(key) {
    if (!days.has(key)) key = dayKey(nearest(pictures, new Date(`${key}T12:00`)).date);
    select(nearest(days.get(key), atTimeOfDay(key, current.date)));
  }

  function showCamera(newCamera) {
    setCamera(newCamera);
    const pool = newCamera ? allPictures.filter((p) => p.camera === newCamera) : allPictures;
    if (pool.length > 0) select(nearest(pool, current.date));
  }

  return (
    <Page>
      {cameraSection}
      <Section>
        <SectionTitle>Gallery</SectionTitle>
        <Toolbar>
          <Group>
            <IconButton
              disabled={dayIndex <= 0}
              onClick={() => showDay(dayKeys[dayIndex - 1])}
            >
              <Chevron left />
            </IconButton>
            <BoxInput
              type="date"
              value={day}
              min={dayKeys[0]}
              max={dayKeys[dayKeys.length - 1]}
              onChange={(e) => e.target.value && showDay(e.target.value)}
            />
            <IconButton
              disabled={dayIndex >= dayKeys.length - 1}
              onClick={() => showDay(dayKeys[dayIndex + 1])}
            >
              <Chevron />
            </IconButton>
          </Group>
          <Group>
            {cameras.length > 1 && (
              <Select value={camera} onChange={(e) => showCamera(e.target.value)}>
                <option value="">All cameras</option>
                {cameras.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            )}
            <ToolButton disabled={current === latest} onClick={() => select(latest)}>
              Latest
            </ToolButton>
          </Group>
        </Toolbar>
        <HourGrid>
          {hours.map((files, h) => {
            const videos = files.filter((p) => p.isVideo).length;
            return (
              <HourBar
                key={h}
                $fill={(100 * files.length) / busiestHour}
                $selected={h === hour}
                disabled={files.length === 0}
                title={`${pad(h)}:00 · ${files.length} files${videos ? `, ${videos} video` : ""}`}
                onClick={() => select(files[0])}
              >
                {videos > 0 && <VideoDot />}
              </HourBar>
            );
          })}
          {hours.map((_, h) => (
            <HourLabel key={h}>{h % 6 === 0 ? pad(h) : ""}</HourLabel>
          ))}
        </HourGrid>
        <HourHeader>
          {pad(hour)}:00–{pad((hour + 1) % 24)}:00 · {hourPictures.length} files
        </HourHeader>
        <Stage
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <Track ref={trackRef}>
            {[prev, current, next].map(
              (picture, i) =>
                picture && (
                  <Pane key={picture.filename} $offset={(i - 1) * 100}>
                    {renderPane(picture)}
                  </Pane>
                )
            )}
          </Track>
          <StepButton $side="left" disabled={index <= 0} onClick={() => step(-1)}>
            <Chevron left />
          </StepButton>
          <StepButton
            $side="right"
            disabled={index >= pictures.length - 1}
            onClick={() => step(1)}
          >
            <Chevron />
          </StepButton>
        </Stage>
        <Caption>
          {current.date.toLocaleString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZoneName: "short",
          })}
          {cameras.length > 1 && ` · ${current.camera}`}
        </Caption>
        <Scrubber
          type="range"
          min={0}
          max={hourPictures.length - 1}
          value={hourPictures.indexOf(current)}
          onChange={(e) => select(hourPictures[e.target.value])}
        />
        <ThumbnailGrid>
          {hourPictures.map((picture) => (
            <Thumbnail
              key={picture.filename}
              filename={picture.filename}
              selected={picture === current}
              onClick={() => select(picture)}
            />
          ))}
        </ThumbnailGrid>
      </Section>
    </Page>
  );
}
